import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';
import { APP_CURRENCY, dualAmount, SecondaryRate } from '../utils/currency';

interface CurrencyContextType {
  /** Loaded EUR->local rate for a non-EUR dealer, else null (EUR-only). */
  rate: SecondaryRate | null;
  /** Amount body without a leading € — "329.00" or "329.00 (≈ 303.80 CHF)". */
  dual: (eur: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType>({
  rate: null,
  dual: (eur: number) => Number(eur).toFixed(2),
});

/**
 * Fetches the dealer's EUR->local rate once (based on the profile currency) and
 * exposes a `dual()` formatter. Non-EUR dealers get the approximate secondary;
 * EUR dealers (and any failure) stay EUR-only. Settlement stays EUR everywhere.
 */
export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [rate, setRate] = useState<SecondaryRate | null>(null);

  const requestedCurrency = new URLSearchParams(window.location.search)
    .get('displayCurrency')
    ?.toUpperCase();
  const currency = (
    user && requestedCurrency && /^[A-Z]{3}$/.test(requestedCurrency)
      ? requestedCurrency
      : (user as any)?.currency || APP_CURRENCY
  )
    .toString()
    .toUpperCase();

  useEffect(() => {
    let cancelled = false;
    if (!currency || currency === APP_CURRENCY) {
      setRate(null);
      return;
    }
    apiService
      .get<{ base: string; rates: Array<{ currency: string; rate: number }> }>(
        '/currency/rates',
        { targetCurrencies: currency },
      )
      .then((res) => {
        if (cancelled) return;
        const match = (res?.rates || []).find(
          (r) => (r.currency || '').toUpperCase() === currency,
        );
        if (match && Number(match.rate) > 0) {
          setRate({ currency, rate: Number(match.rate) });
        }
      })
      .catch(() => {
        // Non-fatal: endpoint not deployed / offline -> stay EUR-only.
      });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  const dual = React.useCallback((eur: number) => dualAmount(eur, rate), [rate]);

  const value = React.useMemo(() => ({ rate, dual }), [rate, dual]);
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => useContext(CurrencyContext);
